import {
    ClientStatus,
    OfficeRequestStatus,
    RecurrenceRule,
    OfficeManagerActions,
    ClientInquiryMethod,
} from "intake_shared/src/types";

export enum CalendarId {
    OFFICE = "office_window_calendar",
    AVAILABILITY = "availability_calendar",
    UNAVAILABLITY = "unavailability_calendar",
    HELD_BOOKINGS = "held_booking_calendar",
    CONFIRMED_BOOKINGS = "confirmed_booking_calendar",
    INTAKE_SESSION_AVAILABILITY = "intake_session_availability_calendar",
}

export enum ViewAs {
    SCHEDULE_VIEW_BOX = "schedule_view_box",
    CALENDAR_VIEW_BOX = "calendar_view_box",
}

export const STATUS_COLORS = {
    [ClientStatus.INCOMPLETE_INQUIRY]: "#B8860B",
    [ClientStatus.INQUIRY]: "#990033",
    [ClientStatus.APPT_HELD]: "#660033",
    [ClientStatus.APPT_CONFIRMED]: "#666633",
    [ClientStatus.INS_VERIFICATION]: "#336600",
    [ClientStatus.FEE_SETTING]: "#669999",
    [ClientStatus.APPT_SETTING]: "#00cc00",
    [ClientStatus.WAITLIST]: "#ffcc00",
    [ClientStatus.DO_NOT_BOOK]: "#cc0000",
    [ClientStatus.INS_FOLLOWUP]: "#ff6666",
    [ClientStatus.COMPLETED]: "#33cc33",
    [ClientStatus.NON_RESPONSIVE]: "",
    [ClientStatus.FOLLOWUP]: "#F08080",
};

export const RECURRENCE_COLORS: { [key: string]: string } = {
    [RecurrenceRule.WEEKLY.toLowerCase()]: "#5F9EA0",
    [RecurrenceRule.BIWEEKLY.toLowerCase()]: "#B8860B",
    "2x/weekly": "#93AACC",
    [RecurrenceRule.TRIWEEKLY.toLowerCase()]: "#F08080",
    [RecurrenceRule.MONTHLY.toLowerCase()]: "#708090",
    [RecurrenceRule.ONE_OFF.toLowerCase()]: "#198988",
    [RecurrenceRule.TWO_MONTHS.toLowerCase()]: "#2867b5",
    [RecurrenceRule.THREE_MONTHS.toLowerCase()]: "#39209e",
};

export const PRIORITY_COLORS: { [key: string]: string } = {
    1: "#5F9EA0",
    2: "#F08080",
    3: "#708090",
    4: "#FF0000",
};

export const TICKETS_STATUS_COLORS: { [key: string]: string } = {
    1: "#33cc33",
    2: "#cc0000",
    3: "#007bff",
    4: "#B8860B",
    5: "#708090",
    6: "#2867b5",
};

export const OFFICE_REQUEST_STATUS_COLORS = {
    [OfficeRequestStatus.APPROVED]: "#33cc33",
    [OfficeRequestStatus.REJECTED]: "#cc0000",
    [OfficeRequestStatus.PENDING]: "#669999",
    [OfficeRequestStatus.APPROVED_WITH_CHANGES]: "#708090",
    [OfficeRequestStatus.HELD]: "#007bff",
    [OfficeRequestStatus.ON_WAITLIST]: "#B8860B",
};

export const OFFICE_MANAGER_ACTIVITIES_COLORS = {
    [OfficeManagerActions.APPROVED_REQUEST]: "#33cc33",
    [OfficeManagerActions.REJECTED_REQUEST]: "#cc0000",
    [OfficeManagerActions.DELETED_OFFICE_WINDOW]: "#cc0000",
    [OfficeManagerActions.APPROVED_WITH_CHANGES]: "#708090",
    [OfficeManagerActions.CREATED_OFFICE_WINDOW]: "#669999",
    [OfficeManagerActions.HELD_WINDOW]: "#007bff",
    [OfficeManagerActions.HELD_WINDOW_WITH_CHANGES]: "#007bff",
    [OfficeManagerActions.APPROVED_OFFICE_WINDOW_DELETE_REQUEST]: "#39209e",
    [OfficeManagerActions.REJECTED_OFFICE_WINDOW_DELETE_REQUEST]: "#cc0000",
};

export const INQIRY_METHOD_COLOR = {
    [ClientInquiryMethod.CHOOSING_THERAPY]: "#33cc33",
    [ClientInquiryMethod.EMAIL]: "#990033",
    [ClientInquiryMethod.GOOD_THERAPY]: "#39209e",
    [ClientInquiryMethod.INSURANCE_CHECK]: "#2867b5",
    [ClientInquiryMethod.INTERNAL]: "#007bff",
    [ClientInquiryMethod.MENTAL_HEALTH_MATCH]: "#666633",
    [ClientInquiryMethod.PHONE]: "#33cc33",
    [ClientInquiryMethod.PSYCH_TODAY]: "#666633",
    [ClientInquiryMethod.SUBWAYS_ADS]: "#5F9EA0",
    [ClientInquiryMethod.THERAPY_DEN]: "#708090",
    [ClientInquiryMethod.THERAPY_FOR_LATINX]: "#198988",
    [ClientInquiryMethod.THERAPY_FOR_BLACK_GIRLS]: "#336600",
    [ClientInquiryMethod.WEB_FORM]: "#ffcc00",
    [ClientInquiryMethod.WEB_FORM_INCOMPLETE]: "#b54708",
    [ClientInquiryMethod.PARKHILL_WEBFORM]: "#007bff",
    [ClientInquiryMethod.PARKHILL_WEBFORM_INCOMPLETE]: "#93370d",
    [ClientInquiryMethod.ZDOC]: "#198988",
    [ClientInquiryMethod.MOVE]: "#00cc00",
    [ClientInquiryMethod.WELL_TRACK]: "#F08080",
};

export const getClientRowStylesForInquiryMethod = (
    method?: string
): Record<string, string> =>
    method?.trim().toLowerCase() === ClientInquiryMethod.ZDOC.toLowerCase()
        ? {
              backgroundColor: "#FFF59D",
              "--table-row-hover-background": "#FFEE58",
          }
        : {};

export function getStatusLabel(code) {
    const statusMap = {
        2: "Open",
        3: "Pending",
        4: "Resolved",
        5: "Closed",
        6: "Waiting on Third Party",
    };

    return statusMap[code] || "Unknown Status";
}

export function getPriorityLabel(code) {
    const priorityMap = {
        1: "Low",
        2: "Medium",
        3: "High",
        4: "Urgent",
    };

    return priorityMap[code] || "Unknown Priority";
}

export const getColorStylesForCalendarId = (calendarId) => {
    switch (calendarId) {
        case CalendarId.OFFICE:
            return {
                backgroundColor: "rgba(125, 38, 207, 0.7)",
            };
        case CalendarId.AVAILABILITY:
            return {
                backgroundColor: "rgba(24, 118, 240, 0.7)",
            };
        case CalendarId.HELD_BOOKINGS:
            return {
                backgroundColor: "rgba(204, 204, 0, 0.7)",
            };
        case CalendarId.CONFIRMED_BOOKINGS:
            return {
                backgroundColor: "rgba(35, 209, 96, 0.7)",
            };
        case CalendarId.INTAKE_SESSION_AVAILABILITY:
            return {
                backgroundColor: "rgba(229, 57, 53, 0.7)",
            };
        default:
            return {
                backgroundColor: "rgba(125, 38, 207, 0.7)",
            };
    }
};

export const getV2ColorStylesForCalendarId = (calendarId) => {
    // Handle office-specific IDs
    if (typeof calendarId === "string" && calendarId.startsWith("office-")) {
        const officeNumber = calendarId.replace("office-", "");

        // Office-specific color mappings
        const officeColors = {
            "12060": {
                backgroundColor: "rgba(255, 87, 51, 0.15)",
                titleColor: "#ff5733",
                borderColor: "#ff5733",
            },
            "328": {
                backgroundColor: "rgba(204, 204, 0, 0.15)",
                titleColor: "#cccc00",
                borderColor: "#cccc00",
            },
            "1": {
                backgroundColor: "rgba(51, 87, 255, 0.15)",
                titleColor: "#3357ff",
                borderColor: "#3357ff",
            },
            "2": {
                backgroundColor: "rgba(243, 51, 255, 0.15)",
                titleColor: "#f333ff",
                borderColor: "#f333ff",
            },
            "3": {
                backgroundColor: "rgba(255, 51, 161, 0.15)",
                titleColor: "#ff33a1",
                borderColor: "#ff33a1",
            },
            "128": {
                backgroundColor: "rgba(125, 38, 207, 0.15)",
                titleColor: "#7d26cd",
                borderColor: "#7d26cd",
            },
            "507": {
                backgroundColor: "rgba(255, 140, 51, 0.15)",
                titleColor: "#ff8c33",
                borderColor: "#ff8c33",
            },
            "518": {
                backgroundColor: "rgba(51, 255, 140, 0.15)",
                titleColor: "#33ff8c",
                borderColor: "#33ff8c",
            },
            "519": {
                backgroundColor: "rgba(140, 51, 255, 0.15)",
                titleColor: "#8c33ff",
                borderColor: "#8c33ff",
            },
            "4": {
                backgroundColor: "rgba(255, 99, 71, 0.15)",
                titleColor: "#ff6347",
                borderColor: "#ff6347",
            },
            "5": {
                backgroundColor: "rgba(51, 255, 87, 0.15)",
                titleColor: "#33ff57",
                borderColor: "#33ff57",
            },
            "6": {
                backgroundColor: "rgba(0, 153, 153, 0.15)",
                titleColor: "#009999",
                borderColor: "#009999",
            },
            "7": {
                backgroundColor: "rgba(243, 134, 48, 0.15)",
                titleColor: "#f38630",
                borderColor: "#f38630",
            },
            "8": {
                backgroundColor: "rgba(102, 51, 153, 0.15)",
                titleColor: "#663399",
                borderColor: "#663399",
            },
            "9": {
                backgroundColor: "rgba(0, 191, 255, 0.15)",
                titleColor: "#00bfff",
                borderColor: "#00bfff",
            },
            "10": {
                backgroundColor: "rgba(255, 105, 180, 0.15)",
                titleColor: "#ff69b4",
                borderColor: "#ff69b4",
            },
            "11": {
                backgroundColor: "rgba(34, 193, 195, 0.15)",
                titleColor: "#22c1c3",
                borderColor: "#22c1c3",
            },
            "12": {
                backgroundColor: "rgba(251, 188, 5, 0.15)",
                titleColor: "#fbcb05",
                borderColor: "#fbcb05",
            },
            "13": {
                backgroundColor: "rgba(45, 204, 112, 0.15)",
                titleColor: "#2dcc70",
                borderColor: "#2dcc70",
            },
            "345": {
                backgroundColor: "rgba(130, 143, 255, 0.15)",
                titleColor: "#828fff",
                borderColor: "#828fff",
            },
            "103": {
                backgroundColor: "rgba(255, 160, 122, 0.15)",
                titleColor: "#ffa07a",
                borderColor: "#ffa07a",
            },
            "104": {
                backgroundColor: "rgba(60, 179, 113, 0.15)",
                titleColor: "#3cb371",
                borderColor: "#3cb371",
            },
            "105": {
                backgroundColor: "rgba(106, 90, 205, 0.15)",
                titleColor: "#6a5acd",
                borderColor: "#6a5acd",
            },
            "206": {
                backgroundColor: "rgba(255, 69, 0, 0.15)",
                titleColor: "#ff4500",
                borderColor: "#ff4500",
            },
        };

        return (
            officeColors[officeNumber] || {
                backgroundColor: "rgba(186, 236, 209, 0.15)",
                titleColor: "var(--color-primary-v2)",
                borderColor: "var(--color-primary-v2)",
            }
        );
    }

    // Handle standard calendar IDs
    switch (calendarId) {
        case CalendarId.OFFICE:
            return {
                backgroundColor: "#F2EEFE",
                titleColor: "#714ADC",
                borderColor: "#D7C9FC",
            };
        case CalendarId.AVAILABILITY:
            return {
                backgroundColor: "#EDF9FF",
                titleColor: "#2F7DF5",
                borderColor: "#C6ECFF",
            };
        case CalendarId.HELD_BOOKINGS:
            return {
                backgroundColor: "#FEF8E9",
                titleColor: "#DDA31B",
                borderColor: "#FCE8B9",
            };
        case CalendarId.CONFIRMED_BOOKINGS:
            return {
                backgroundColor: "#E9F9F0",
                titleColor: "#1CAE60",
                borderColor: "#BAECD1",
            };
        case CalendarId.INTAKE_SESSION_AVAILABILITY:
            return {
                backgroundColor: "#E53935",
                titleColor: "#FFFFFF",
                borderColor: "#B71C1C",
            };
        default:
            return {
                backgroundColor: "#F2EEFE",
                titleColor: "#714ADC",
            };
    }
};

export const getBadgeColorByPractice = (practice) => {
    let variant;

    switch (practice) {
        case "CHP":
            variant = "info";
            break;
        case "HT":
            variant = "primary";
            break;
        case "PH":
            variant = "success";
            break;
        default:
            variant = "danger";
    }
    return variant;
};

export const getV2BadgePropsByPractice = (practice) => {
    let bgColor;
    let textColor;

    switch (practice) {
        case "CHP":
            bgColor = "#E3F6FF";
            textColor = "#0E121B";
            break;
        case "HT":
            bgColor = "#B8D4DC";
            textColor = "#0C343F";
            break;
        case "PH":
            bgColor = "#BAECD1";
            textColor = "#0E5730";
            break;
        default:
            bgColor = "#B8D4DC";
            textColor = "#0C343F";
    }
    return { bgColor, textColor };
};

export const getColorStylesForClientStatus = (status: string) => {
    switch (status) {
        case ClientStatus.INCOMPLETE_INQUIRY:
            return { bgColor: "#B8860B", textColor: "#712249" };
        case ClientStatus.INQUIRY:
            return { bgColor: "#990033", textColor: "#ffffff" };
        case ClientStatus.APPT_HELD:
            return { bgColor: "#4a2e9e", textColor: "#0E5730" };
        case ClientStatus.APPT_CONFIRMED:
            return { bgColor: "#067647", textColor: "#711920" };
        case ClientStatus.INS_VERIFICATION:
            return { bgColor: "#336600", textColor: "#38256E" };
        case ClientStatus.FEE_SETTING:
            return { bgColor: "#669999", textColor: "#6D440E" };
        case ClientStatus.APPT_SETTING:
            return { bgColor: "#00cc00", textColor: "#33363B" };
        case ClientStatus.WAITLIST:
            return { bgColor: "#ffcc00", textColor: "#33363B" };
        case ClientStatus.DO_NOT_BOOK:
            return { bgColor: "#cc0000", textColor: "#33363B" };
        case ClientStatus.INS_FOLLOWUP:
            return { bgColor: "#ff6666", textColor: "#33363B" };
        case ClientStatus.COMPLETED:
            return { bgColor: "#33cc33", textColor: "#33363B" };
        case ClientStatus.FOLLOWUP:
            return { bgColor: "#F08080", textColor: "#33363B" };
        default:
            return { bgColor: "#D3D5D9", textColor: "#33363B" };
    }
};

export const getColorStylesForInquiryMethod = (method: string) => {
    switch (method) {
        case ClientInquiryMethod.CHOOSING_THERAPY:
            return { bgColor: "#33cc33", textColor: "#712249" };
        case ClientInquiryMethod.EMAIL:
            return { bgColor: "#990033", textColor: "#205773" };
        case ClientInquiryMethod.GOOD_THERAPY:
            return { bgColor: "#39209e", textColor: "#0E5730" };
        case ClientInquiryMethod.INSURANCE_CHECK:
            return { bgColor: "#2867b5", textColor: "#711920" };
        case ClientInquiryMethod.INTERNAL:
            return { bgColor: "#007bff", textColor: "#ffffff" };
        case ClientInquiryMethod.MENTAL_HEALTH_MATCH:
            return { bgColor: "#666633", textColor: "#ffffff" };
        case ClientInquiryMethod.PHONE:
            return { bgColor: "#33cc33", textColor: "#000000" };
        case ClientInquiryMethod.PSYCH_TODAY:
            return { bgColor: "#666633", textColor: "#ffffff" };
        case ClientInquiryMethod.SUBWAYS_ADS:
            return { bgColor: "#5F9EA0", textColor: "#000000" };
        case ClientInquiryMethod.THERAPY_DEN:
            return { bgColor: "#708090", textColor: "#ffffff" };
        case ClientInquiryMethod.THERAPY_FOR_LATINX:
            return { bgColor: "#198988", textColor: "#ffffff" };
        case ClientInquiryMethod.THERAPY_FOR_BLACK_GIRLS:
            return { bgColor: "#336600", textColor: "#ffffff" };
        case ClientInquiryMethod.WEB_FORM:
            return { bgColor: "#ffcc00", textColor: "#000000" };
        case ClientInquiryMethod.WEB_FORM_INCOMPLETE:
            return { bgColor: "#b54708", textColor: "#ffffff" };
        case ClientInquiryMethod.PARKHILL_WEBFORM:
            return { bgColor: "#007bff", textColor: "#ffffff" };
        case ClientInquiryMethod.PARKHILL_WEBFORM_INCOMPLETE:
            return { bgColor: "#93370d", textColor: "#ffffff" };
        case ClientInquiryMethod.ZDOC:
            return { bgColor: "#198988", textColor: "#ffffff" };
        case ClientInquiryMethod.MOVE:
            return { bgColor: "#00cc00", textColor: "#000000" };
        case ClientInquiryMethod.WELL_TRACK:
            return { bgColor: "#F08080", textColor: "#000000" };
        default:
            return { bgColor: "#666633", textColor: "#ffffff" };
    }
};
